import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { t } = useShop();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError(t.auth.passwordMismatch);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: form.password });
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.auth.resetInvalid);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="reset-password-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-brown/10">
        <Link to="/" className="flex flex-col leading-none" data-testid="reset-logo">
          <span className="font-serif text-2xl font-medium tracking-tight text-brown">VLP Dolap</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brown-muted mt-1">Volpar</span>
        </Link>
        <Link
          to="/giris"
          className="inline-flex items-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
          data-testid="reset-back-login"
        >
          <ArrowLeft size={15} /> {t.auth.backToLogin}
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <h1 className="font-serif text-3xl font-medium text-brown mb-10">{t.auth.resetTitle}</h1>

          {status === "success" ? (
            <div className="border border-olive/30 bg-olive/5 p-6" data-testid="reset-success">
              <p className="text-sm text-brown leading-relaxed">{t.auth.resetSuccess}</p>
              <Link
                to="/giris"
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-olive-dark hover:text-brown transition-colors duration-300"
                data-testid="reset-success-login-link"
              >
                {t.auth.backToLogin} →
              </Link>
            </div>
          ) : !token ? (
            <p className="text-sm text-red-800 bg-red-800/5 border border-red-800/15 px-4 py-3" data-testid="reset-no-token">
              {t.auth.resetInvalid}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-8" data-testid="reset-form">
              <div>
                <label htmlFor="reset-password" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                  {t.auth.newPassword}
                </label>
                <Input
                  id="reset-password"
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="editorial-input mt-1"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                  {t.auth.confirmPassword}
                </label>
                <Input
                  id="reset-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={form.confirm}
                  onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                  className="editorial-input mt-1"
                  data-testid="reset-confirm-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-800 bg-red-800/5 border border-red-800/15 px-4 py-3" data-testid="reset-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
                data-testid="reset-submit-button"
              >
                {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                {t.auth.resetCta}
              </button>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
